import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';

import { useTranslation } from 'react-i18next';
import type { AutopilotItemContentsResponse, AutopilotQueueItem } from '../types/autopilotDashboard';
import {
  fetchAutopilotDashboard,
  fetchAutopilotItemContents,
  importJiraReportedItems,
  setAutopilotItemReviewStatus,
} from '../services/api/autopilotDashboardService';
import { trackAnalyticsEvent } from '../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../services/eventTaxonomy';
import type {
  AutopilotItemStatus,
  AutopilotItemTabName,
  AutopilotLinkType,
  AutopilotQueueMode,
  AutopilotRefreshTrigger,
} from '../services/eventTypes';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../utils/iconFallbacks';
function deriveQueueMode(mode?: string): AutopilotQueueMode {
  if (mode === 'git_ref') return 'git_ref';
  if (mode === 'working_tree_fallback') return 'fallback';
  return 'working_tree';
}

const ALLOWED_STATUS_VALUES: ReadonlySet<AutopilotItemStatus> = new Set<AutopilotItemStatus>([
  'reported',
  'queued',
  'ready_for_review',
  'done_no_merge',
  'blocked',
  'clear',
]);

function toAnalyticsStatus(status: string): AutopilotItemStatus | null {
  return ALLOWED_STATUS_VALUES.has(status as AutopilotItemStatus)
    ? (status as AutopilotItemStatus)
    : null;
}

type ContentsDialogState =
  | { open: false }
  | {
      open: true;
      itemId: string;
      itemTitle: string;
      kind: 'prd' | 'tasks';
      loading: boolean;
      error?: string;
      contents?: AutopilotItemContentsResponse;
    };

function statusCategory(status: string): 'reported' | 'queue' | 'in_progress' | 'review' | 'completed' {
  if (status === 'reported') return 'reported';
  if (status === 'in_progress') return 'in_progress';
  if (status === 'ready_for_review') return 'review';
  if (status === 'done' || status === 'done_no_merge' || status === 'completed' || status === 'skipped') return 'completed';
  return 'queue';
}

function statusColor(status: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (status === 'in_progress') return 'warning';
  if (status === 'done' || status === 'completed') return 'success';
  if (status === 'done_no_merge' || status === 'skipped') return 'warning';
  if (status.includes('fail') || status.includes('error')) return 'error';
  if (status === 'reported' || status === 'queued' || status === 'ready_for_review') return 'info';
  return 'default';
}

function toOneLine(input: string): string {
  return input.replace(/\s+/g, ' ').trim();
}

function truncate(input: string, maxLen: number): string {
  if (input.length <= maxLen) return input;
  return `${input.slice(0, Math.max(0, maxLen - 3))}...`;
}

function formatDate(input?: string): string {
  if (!input) return '';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return input;
  return date.toLocaleString();
}

function sortByWsjfDesc(a: AutopilotQueueItem, b: AutopilotQueueItem): number {
  const aScore = Number(a.wsjf?.score ?? 0);
  const bScore = Number(b.wsjf?.score ?? 0);
  if (aScore !== bScore) return bScore - aScore;
  const aTc = Number(a.wsjf?.time_criticality ?? 0);
  const bTc = Number(b.wsjf?.time_criticality ?? 0);
  if (aTc !== bTc) return bTc - aTc;
  return String(a.id).localeCompare(String(b.id));
}

type DetailsTab = 'overview' | 'commits' | 'files' | 'history';

export default function AutopilotDashboardPage() {
  const { t } = useTranslation('autopilot');
  const [loading, setLoading] = useState(true);
  const [importingJira, setImportingJira] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jiraImportSummary, setJiraImportSummary] = useState<string | null>(null);
  const [items, setItems] = useState<AutopilotQueueItem[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [baseBranch, setBaseBranch] = useState<string | null>(null);
  const [queueSource, setQueueSource] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTab, setDetailsTab] = useState<DetailsTab>('overview');

  const abortRef = useRef<AbortController | null>(null);
  const autoImportAttemptedRef = useRef(false);
  const [contentsDialog, setContentsDialog] = useState<ContentsDialogState>({ open: false });

  const load = useCallback(async (trigger: AutopilotRefreshTrigger = 'button') => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_dashboard_refreshed, {
      trigger,
      timestamp: new Date().toISOString(),
    });
    const startedAt = Date.now();
    try {
      const dashboard = await fetchAutopilotDashboard({
        signal: controller.signal,
        maxCommits: 25,
        maxFiles: 250,
      });
      setItems(dashboard.queue.items);
      setGeneratedAt(dashboard.generated_at);
      setBaseBranch(dashboard.repo.base_branch);
      const source = dashboard.repo.queue_source;
      if (source?.mode === 'git_ref' && source?.resolved_ref) {
        setQueueSource(t('queueSource.static', { ref: source.resolved_ref }));
      } else if (source?.mode === 'working_tree_fallback' && source?.resolved_ref) {
        setQueueSource(t('queueSource.fallback', { ref: source.resolved_ref }));
      } else {
        setQueueSource(t('queueSource.workingTree'));
      }
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_dashboard_fetched, {
        items_count: dashboard.queue.items.length,
        queue_mode: deriveQueueMode(source?.mode),
        duration_ms: Date.now() - startedAt,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : t('errors.loadDashboard');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const runJiraImport = useCallback(async (showError: boolean) => {
    setImportingJira(true);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_jira_import_started, {
      timestamp: new Date().toISOString(),
    });
    const startedAt = Date.now();
    try {
      const result = await importJiraReportedItems({
        project_key: 'CAB',
        label: '',
        issue_type: 'Task',
      });
      setJiraImportSummary(
        t('summary.jiraImport', {
          imported: result.imported,
          updated: result.updated,
          skipped: result.skipped,
          total: result.total_processed,
        })
      );
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_jira_import_completed, {
        imported: result.imported,
        updated: result.updated,
        skipped: result.skipped,
        total_processed: result.total_processed,
        duration_ms: Date.now() - startedAt,
        timestamp: new Date().toISOString(),
      });
      return result;
    } catch (e) {
      const message = e instanceof Error ? e.message : t('errors.importJira');
      if (showError) setError(message);
      setJiraImportSummary(null);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_jira_import_failed, {
        feature_area: 'autopilot_jira_import',
        error_category: 'unknown',
        error_code: message.slice(0, 120),
        retryable: true,
        duration_ms: Date.now() - startedAt,
        timestamp: new Date().toISOString(),
      });
      return null;
    } finally {
      setImportingJira(false);
    }
  }, [t]);

  useEffect(() => {
    const initialize = async () => {
      if (!autoImportAttemptedRef.current) {
        autoImportAttemptedRef.current = true;
        await runJiraImport(false);
      }
      await load('mount');
    };
    void initialize();
    return () => abortRef.current?.abort();
  }, [load, runJiraImport]);

  const grouped = useMemo(() => {
    const groups: Record<'reported' | 'queue' | 'in_progress' | 'review' | 'completed', AutopilotQueueItem[]> = {
      reported: [],
      queue: [],
      in_progress: [],
      review: [],
      completed: [],
    };
    for (const item of items) {
      groups[statusCategory(item.status)].push(item);
    }
    groups.reported.sort(sortByWsjfDesc);
    groups.queue.sort(sortByWsjfDesc);
    groups.in_progress.sort(sortByWsjfDesc);
    groups.review.sort(sortByWsjfDesc);
    groups.completed.sort(sortByWsjfDesc);
    return groups;
  }, [items]);

  const itemsById = useMemo(() => {
    const map = new Map<string, AutopilotQueueItem>();
    for (const item of items) map.set(item.id, item);
    return map;
  }, [items]);

  const selectedItem = useMemo(() => {
    if (!selectedItemId) return null;
    return itemsById.get(selectedItemId) ?? null;
  }, [itemsById, selectedItemId]);

  const openContents = useCallback(async (item: AutopilotQueueItem, kind: 'prd' | 'tasks') => {
    setContentsDialog({ open: true, itemId: item.id, itemTitle: item.title, kind, loading: true });
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_artifact_viewed, {
      item_id: item.id,
      artifact_type: kind,
      source: 'inline',
      outcome: 'started',
      timestamp: new Date().toISOString(),
    });
    try {
      const contents = await fetchAutopilotItemContents(item.id, { maxChars: 400_000 });
      setContentsDialog({ open: true, itemId: item.id, itemTitle: item.title, kind, loading: false, contents });
      const bytesLoaded = kind === 'prd' ? contents.prd.size_bytes : contents.tasks.size_bytes;
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_artifact_viewed, {
        item_id: item.id,
        artifact_type: kind,
        source: 'inline',
        outcome: 'succeeded',
        bytes_loaded: bytesLoaded,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : t('errors.loadArtifact');
      setContentsDialog({ open: true, itemId: item.id, itemTitle: item.title, kind, loading: false, error: message });
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_artifact_viewed, {
        item_id: item.id,
        artifact_type: kind,
        source: 'inline',
        outcome: 'failed',
        timestamp: new Date().toISOString(),
      });
    }
  }, [t]);

  const closeContents = useCallback(() => setContentsDialog({ open: false }), []);

  const updateItemStatus = useCallback(
    async (itemId: string, status: string, reason?: string) => {
      setUpdatingStatus(`${itemId}:${status}`);
      setError(null);
      const analyticsStatus = toAnalyticsStatus(status);
      if (analyticsStatus) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_item_status_updated, {
          item_id: itemId,
          new_status: analyticsStatus,
          has_reason: Boolean(reason && reason.trim()),
          outcome: 'started',
          timestamp: new Date().toISOString(),
        });
      }
      try {
        await setAutopilotItemReviewStatus(itemId, { status, reason });
        await load();
        if (analyticsStatus) {
          trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_item_status_updated, {
            item_id: itemId,
            new_status: analyticsStatus,
            has_reason: Boolean(reason && reason.trim()),
            outcome: 'succeeded',
            timestamp: new Date().toISOString(),
          });
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : t('errors.updateStatus');
        setError(message);
        if (analyticsStatus) {
          trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_item_status_updated, {
            item_id: itemId,
            new_status: analyticsStatus,
            has_reason: Boolean(reason && reason.trim()),
            outcome: 'failed',
            timestamp: new Date().toISOString(),
          });
        }
      } finally {
        setUpdatingStatus(null);
      }
    },
    [load, t]
  );

  const openDetails = useCallback((item: AutopilotQueueItem) => {
    setSelectedItemId(item.id);
    setDetailsTab('overview');
    setDetailsOpen(true);
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_item_opened, {
      item_id: item.id,
      item_status: item.status,
      wsjf: Number(item.wsjf?.score ?? 0),
      in_queue: statusCategory(item.status) === 'queue',
      timestamp: new Date().toISOString(),
    });
  }, []);

  const handleDetailsTabChange = useCallback((next: AutopilotItemTabName) => {
    setDetailsTab((previous) => {
      if (previous !== next && selectedItemId) {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_item_tab_changed, {
          item_id: selectedItemId,
          tab_name: next,
          previous_tab: previous,
          timestamp: new Date().toISOString(),
        });
      }
      return next;
    });
  }, [selectedItemId]);

  const trackExternalLink = useCallback(
    (itemId: string, linkType: AutopilotLinkType) => {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.autopilot_external_link_opened, {
        item_id: itemId,
        link_type: linkType,
        timestamp: new Date().toISOString(),
      });
    },
    []
  );

  const renderCard = useCallback(
    (item: AutopilotQueueItem, indexInColumn: number) => {
      const title = item.title || item.slug || item.id;
      const branch = item.artifacts.branch || item.git.branch || '';
      const ideaType = item.idea?.type?.trim() ?? '';
      const ideaCategory = item.idea?.category?.trim() ?? '';
      const ideaAreas = Array.isArray(item.idea?.areas) ? item.idea!.areas : [];
      const isNextUp = item.status === 'queued' && indexInColumn === 0;
      const summaryPreview = truncate(toOneLine(item.summary || ''), 220);
      const reporter = (item.reported_by || '').trim();
      const reportedAt = formatDate(item.reported_at);
      const jiraKey = item.jira?.issue_key?.trim() || '';
      const prdReady = item.jira?.ready_for_prd === true;

      return (
        <Card
          key={item.id}
          variant="outlined"
          sx={{
            borderRadius: 2,
            borderColor: isNextUp ? 'primary.main' : 'rgba(0,0,0,0.12)',
            boxShadow: isNextUp ? '0 10px 24px rgba(25, 118, 210, 0.18)' : 'none',
            bgcolor: isNextUp ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
            overflow: 'hidden',
          }}
        >
          <CardActionArea onClick={() => openDetails(item)}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0, flex: 1 }}>
                    <Chip label={item.status} color={statusColor(item.status)} size="small" />
                    {isNextUp ? <Chip label={t('card.nextUp')} color="primary" size="small" /> : null}
                    <Typography
                      variant="subtitle2"
                      sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                    >
                      {item.id}
                    </Typography>
                  </Stack>
                  <Chip label={t('card.wsjf', { score: Number(item.wsjf?.score ?? 0).toFixed(2) })} size="small" variant="outlined" sx={{ flexShrink: 0 }} />
                </Stack>

                <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25, wordBreak: 'break-word' }}>
                  {title}
                </Typography>
                {summaryPreview ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.35,
                    }}
                  >
                    {summaryPreview}
                  </Typography>
                ) : null}

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ color: 'text.secondary' }}>
                  {jiraKey ? (
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', whiteSpace: 'nowrap' }}
                    >
                      {jiraKey}
                    </Typography>
                  ) : null}
                  {prdReady ? <Chip label={t('card.prdReady')} size="small" color="success" /> : null}
                  {item.jira?.confluence_page_url ? <Chip label={t('card.confluencePrd')} size="small" variant="outlined" /> : null}
                  {item.jira?.confluence_tasks_page_url ? <Chip label={t('card.confluenceTasks')} size="small" variant="outlined" /> : null}
                  {reporter ? (
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      {t('card.reportedBy', { reporter })}
                    </Typography>
                  ) : null}
                  {reportedAt ? (
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      {reportedAt}
                    </Typography>
                  ) : null}
                  {branch ? (
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        wordBreak: 'break-all',
                      }}
                    >
                      {branch}
                    </Typography>
                  ) : null}
                  {item.artifacts.prd_path ? (
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      {t('card.prd')}
                    </Typography>
                  ) : null}
                  {item.artifacts.tasks_path ? (
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      {t('card.tasks')}
                    </Typography>
                  ) : null}
                  {item.artifacts.pr_url?.trim() ? (
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      {t('card.pr')}
                    </Typography>
                  ) : null}
                  {item.deps?.length ? (
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                      {t('card.deps', { count: item.deps.length })}
                    </Typography>
                  ) : null}
                </Stack>

                {(ideaType || ideaCategory || ideaAreas.length) ? (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ pt: 0.25 }}>
                    {ideaType ? <Chip size="small" variant="outlined" label={t('card.type', { value: ideaType })} /> : null}
                    {ideaCategory ? <Chip size="small" variant="outlined" label={t('card.category', { value: ideaCategory })} /> : null}
                    {ideaAreas.slice(0, 4).map((a) => (
                      <Chip key={`area:${a}`} size="small" variant="outlined" label={t('card.area', { value: a })} />
                    ))}
                    {ideaAreas.length > 4 ? <Chip size="small" variant="outlined" label={t('card.moreAreas', { count: ideaAreas.length - 4 })} /> : null}
                  </Stack>
                ) : null}
              </Stack>
            </CardContent>
          </CardActionArea>
        </Card>
      );
    },
    [openDetails, t]
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('page.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {generatedAt ? t('page.updated', { timestamp: new Date(generatedAt).toLocaleString() }) : t('page.placeholder')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {queueSource}
          </Typography>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          onClick={() =>
            void (async () => {
              setError(null);
              await runJiraImport(true);
              await load();
            })()
          }
          disabled={loading || importingJira}
        >
          {importingJira ? t('actions.importingJira') : t('actions.importJira')}
        </Button>
        <Button variant="outlined" startIcon={<Icon size={20} name="arrows-rotate" />} onClick={() => void load('button')}>
          {t('actions.refresh')}
        </Button>
      </Stack>

      {jiraImportSummary ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {jiraImportSummary}
        </Alert>
      ) : null}

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2">{t('loading')}</Typography>
        </Stack>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2} columns={{ xs: 12, md: 15 }}>
        <Grid item xs={12} md={3}>
          <Stack spacing={1}>
            <Typography variant="h6">{t('columns.reported')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('columns.itemCount', { count: grouped.reported.length })}
            </Typography>
            <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', pr: 0.5 }}>
              <Stack spacing={1}>
                {grouped.reported.length ? grouped.reported.map((item, idx) => renderCard(item, idx)) : <Typography color="text.secondary">{t('page.placeholder')}</Typography>}
              </Stack>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={3}>
          <Stack spacing={1}>
            <Typography variant="h6">{t('columns.queue')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('columns.itemCount', { count: grouped.queue.length })}
            </Typography>
            <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', pr: 0.5 }}>
              <Stack spacing={1}>
                {grouped.queue.length ? grouped.queue.map((item, idx) => renderCard(item, idx)) : <Typography color="text.secondary">{t('page.placeholder')}</Typography>}
              </Stack>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={3}>
          <Stack spacing={1}>
            <Typography variant="h6">{t('columns.inProgress')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('columns.itemCount', { count: grouped.in_progress.length })}
            </Typography>
            <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', pr: 0.5 }}>
              <Stack spacing={1}>
                {grouped.in_progress.length ? grouped.in_progress.map((item, idx) => renderCard(item, idx)) : <Typography color="text.secondary">{t('page.placeholder')}</Typography>}
              </Stack>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={3}>
          <Stack spacing={1}>
            <Typography variant="h6">{t('columns.review')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('columns.itemCount', { count: grouped.review.length })}
            </Typography>
            <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', pr: 0.5 }}>
              <Stack spacing={1}>
                {grouped.review.length ? grouped.review.map((item, idx) => renderCard(item, idx)) : <Typography color="text.secondary">{t('page.placeholder')}</Typography>}
              </Stack>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12} md={3}>
          <Stack spacing={1}>
            <Typography variant="h6">{t('columns.completed')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('columns.itemCount', { count: grouped.completed.length })}
            </Typography>
            <Box sx={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', pr: 0.5 }}>
              <Stack spacing={1}>
                {grouped.completed.length ? grouped.completed.map((item, idx) => renderCard(item, idx)) : <Typography color="text.secondary">{t('page.placeholder')}</Typography>}
              </Stack>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Dialog
        open={detailsOpen && Boolean(selectedItem)}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          sx: {
            height: { xs: '100vh', md: '92vh' },
            maxHeight: { xs: '100vh', md: '92vh' },
            borderRadius: { xs: 0, md: 3 },
            overflow: 'hidden',
          },
        }}
      >
        {selectedItem ? (
          <>
            <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
              <Toolbar sx={{ gap: 1 }}>
                <Chip label={selectedItem.status} color={statusColor(selectedItem.status)} size="small" />
                <Typography variant="subtitle2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                  {selectedItem.id}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, minWidth: 0, wordBreak: 'break-word', flex: 1 }}>
                  {selectedItem.title || selectedItem.slug}
                </Typography>
                <Chip label={t('card.wsjf', { score: Number(selectedItem.wsjf?.score ?? 0).toFixed(2) })} size="small" variant="outlined" />
                <IconButton edge="end" onClick={() => setDetailsOpen(false)} aria-label={t('details.close')}>
                  <Icon size={20} name="xmark" />
                </IconButton>
              </Toolbar>
              <Tabs value={detailsTab} onChange={(_, v) => handleDetailsTabChange(v as AutopilotItemTabName)} variant="scrollable" scrollButtons="auto" sx={{ px: 1 }}>
                <Tab icon={<Icon size={20} name="circle-info" />} iconPosition="start" value="overview" label={t('details.tabs.overview')} />
                <Tab icon={<Icon size={20} d={ICON_FALLBACKS.Code} />} iconPosition="start" value="commits" label={t('details.tabs.commits')} />
                <Tab icon={<Icon size={20} name="rectangle-list" />} iconPosition="start" value="files" label={t('details.tabs.files')} />
                <Tab icon={<Icon size={20} name="clock-rotate-left" />} iconPosition="start" value="history" label={t('details.tabs.history')} />
              </Tabs>
            </AppBar>

            <DialogContent sx={{ p: 2 }}>
              {detailsTab === 'overview' ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {t('details.buildSummary')}
                        </Typography>

                        {(selectedItem.idea?.type || selectedItem.idea?.category || selectedItem.idea?.areas?.length) ? (
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 1 }}>
                            {selectedItem.idea?.type ? <Chip size="small" variant="outlined" label={t('card.type', { value: selectedItem.idea.type })} /> : null}
                            {selectedItem.idea?.category ? <Chip size="small" variant="outlined" label={t('card.category', { value: selectedItem.idea.category })} /> : null}
                            {(selectedItem.idea?.areas || []).map((a) => (
                              <Chip key={`sel-area:${a}`} size="small" variant="outlined" label={t('card.area', { value: a })} />
                            ))}
                          </Stack>
                        ) : null}

                        <Stack spacing={0.75}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('details.branch')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                              {selectedItem.artifacts.branch || selectedItem.git.branch || t('page.placeholder')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('details.baseInfo', {
                                base: baseBranch || t('page.placeholder'),
                                ahead: selectedItem.git.ahead,
                                behind: selectedItem.git.behind,
                              })}
                            </Typography>
                          </Box>

                          {selectedItem.deps?.length ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('details.dependencies')}
                              </Typography>
                              <Typography variant="body2">{selectedItem.deps.join(', ')}</Typography>
                            </Box>
                          ) : null}
                          {selectedItem.summary?.trim() ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('details.summary')}
                              </Typography>
                              <Typography variant="body2">{selectedItem.summary}</Typography>
                            </Box>
                          ) : null}
                          {selectedItem.reported_by || selectedItem.reported_at ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('details.reported')}
                              </Typography>
                              <Typography variant="body2">
                                {selectedItem.reported_by || t('page.placeholder')}
                                {selectedItem.reported_at ? ` · ${formatDate(selectedItem.reported_at)}` : ''}
                              </Typography>
                            </Box>
                          ) : null}
                          {selectedItem.jira?.issue_key || selectedItem.jira?.issue_url ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('details.jira')}
                              </Typography>
                              <Typography variant="body2">
                                {selectedItem.jira?.issue_key || t('page.placeholder')}
                                {selectedItem.jira?.issue_url ? ' · ' : ''}
                                {selectedItem.jira?.issue_url ? (
                                  <a
                                    href={selectedItem.jira.issue_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => trackExternalLink(selectedItem.id, 'jira_issue')}
                                  >
                                    {t('details.jiraOpen')}
                                  </a>
                                ) : null}
                              </Typography>
                            </Box>
                          ) : null}
                          {selectedItem.jira?.ready_for_prd !== undefined || selectedItem.jira?.last_readiness_check_at ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('details.intakeState')}
                              </Typography>
                              <Typography variant="body2">
                                {t('details.readyForPrd', { value: selectedItem.jira?.ready_for_prd ? t('details.yes') : t('details.no') })}
                                {selectedItem.jira?.last_readiness_check_at ? ` · ${t('details.checked', { timestamp: formatDate(selectedItem.jira.last_readiness_check_at) })}` : ''}
                              </Typography>
                              {selectedItem.jira?.missing_fields?.length ? (
                                <Typography variant="caption" color="text.secondary">
                                  {t('details.missingFields', { fields: selectedItem.jira.missing_fields.join(', ') })}
                                </Typography>
                              ) : null}
                            </Box>
                          ) : null}
                          {selectedItem.jira?.confluence_page_url || selectedItem.jira?.confluence_tasks_page_url ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('details.confluence')}
                              </Typography>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                {selectedItem.jira?.confluence_page_url ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                      trackExternalLink(selectedItem.id, 'confluence_prd');
                                      window.open(selectedItem.jira!.confluence_page_url!, '_blank', 'noopener,noreferrer');
                                    }}
                                  >
                                    {t('details.openPrd')}
                                  </Button>
                                ) : null}
                                {selectedItem.jira?.confluence_tasks_page_url ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => {
                                      trackExternalLink(selectedItem.id, 'confluence_tasks');
                                      window.open(selectedItem.jira!.confluence_tasks_page_url!, '_blank', 'noopener,noreferrer');
                                    }}
                                  >
                                    {t('details.openTasks')}
                                  </Button>
                                ) : null}
                              </Stack>
                            </Box>
                          ) : null}
                          {selectedItem.status_reason?.trim() ? (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {t('details.reviewNote')}
                              </Typography>
                              <Typography variant="body2">{selectedItem.status_reason}</Typography>
                            </Box>
                          ) : null}

                          <Divider />

                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Button size="small" variant="outlined" startIcon={<Icon size={20} d={ICON_FALLBACKS.Description} />} onClick={() => openContents(selectedItem, 'prd')} disabled={!selectedItem.artifacts.prd_path}>
                              {t('details.viewPrd')}
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<Icon size={20} d={ICON_FALLBACKS.TaskAlt} />} onClick={() => openContents(selectedItem, 'tasks')} disabled={!selectedItem.artifacts.tasks_path}>
                              {t('details.viewTasks')}
                            </Button>
                            {selectedItem.artifacts.pr_url?.trim() ? (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  trackExternalLink(selectedItem.id, 'github_pr');
                                  window.open(selectedItem.artifacts.pr_url.trim(), '_blank', 'noopener,noreferrer');
                                }}
                              >
                                {t('details.openPr')}
                              </Button>
                            ) : null}
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={Boolean(updatingStatus)}
                              onClick={() =>
                                void updateItemStatus(
                                  selectedItem.id,
                                  'reported',
                                  t('details.statusReasons.reported')
                                )
                              }
                            >
                              {t('details.markReported')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={Boolean(updatingStatus)}
                              onClick={() =>
                                void updateItemStatus(
                                  selectedItem.id,
                                  'queued',
                                  t('details.statusReasons.queued')
                                )
                              }
                            >
                              {t('details.moveToQueue')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={Boolean(updatingStatus)}
                              onClick={() => void updateItemStatus(selectedItem.id, 'ready_for_review')}
                            >
                              {t('details.markReady')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={Boolean(updatingStatus)}
                              onClick={() =>
                                void updateItemStatus(
                                  selectedItem.id,
                                  'done_no_merge',
                                  t('details.statusReasons.doneNoMerge')
                                )
                              }
                            >
                              {t('details.completedNoMerge')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={Boolean(updatingStatus)}
                              onClick={() => void updateItemStatus(selectedItem.id, 'blocked', t('details.statusReasons.blocked'))}
                            >
                              {t('details.markBlocked')}
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              disabled={Boolean(updatingStatus)}
                              onClick={() => void updateItemStatus(selectedItem.id, 'clear')}
                            >
                              {t('details.clearOverride')}
                            </Button>
                          </Stack>
                          {selectedItem.status_source ? (
                            <Typography variant="caption" color="text.secondary">
                              {t('details.statusSource', { source: selectedItem.status_source })}
                            </Typography>
                          ) : null}

                          <Stack spacing={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              {t('details.prdPath', { path: selectedItem.artifacts.prd_path || t('page.placeholder') })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('details.tasksPath', { path: selectedItem.artifacts.tasks_path || t('page.placeholder') })}
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {t('details.nextBuild')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {t('details.nextBuildHelp')}
                        </Typography>

                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {t('details.whatChanged')}
                        </Typography>
                        {selectedItem.git.changed_files.length ? (
                          <List dense sx={{ py: 0, maxHeight: 520, overflowY: 'auto' }}>
                            {selectedItem.git.changed_files.slice(0, 300).map((f) => (
                              <ListItem key={`${f.status}:${f.path}`} disableGutters>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                      {f.status} {f.path}
                                    </Typography>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('details.noFileChanges')}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              ) : null}

              {detailsTab === 'commits' ? (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('details.commits')}
                    </Typography>
                    {selectedItem.git.exists ? (
                      selectedItem.git.commits.length ? (
                        <List dense sx={{ py: 0, maxHeight: 720, overflowY: 'auto' }}>
                          {selectedItem.git.commits.slice(0, 500).map((c) => (
                            <ListItem key={c.hash} disableGutters>
                              <ListItemText
                                primary={<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c.hash.slice(0, 8)}</Typography>}
                                secondary={
                                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                    {c.subject} - {formatDate(c.date)}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('details.noCommits')}
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('details.branchNotFound')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ) : null}

              {detailsTab === 'files' ? (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('details.filesEdited')}
                    </Typography>
                    {selectedItem.git.changed_files.length ? (
                      <List dense sx={{ py: 0, maxHeight: 720, overflowY: 'auto' }}>
                        {selectedItem.git.changed_files.slice(0, 2000).map((f) => (
                          <ListItem key={`${f.status}:${f.path}`} disableGutters>
                            <ListItemText primary={<Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{f.status} {f.path}</Typography>} />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('details.noFileChanges')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ) : null}

              {detailsTab === 'history' ? (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t('details.history')}
                    </Typography>
                    {selectedItem.history?.length ? (
                      <Stack spacing={1}>
                        {selectedItem.history
                          .slice()
                          .reverse()
                          .map((h, idx) => {
                            const atText = formatDate(typeof h.at === 'string' ? h.at : undefined);
                            const errorText = typeof h.error === 'string' ? h.error : '';
                            const preview = errorText ? truncate(toOneLine(errorText), 240) : '';
                            return (
                              <Card
                                key={`${selectedItem.id}-history-${idx}`}
                                variant="outlined"
                                sx={{ borderRadius: 2, borderColor: errorText ? 'rgba(211, 47, 47, 0.35)' : 'rgba(0,0,0,0.12)' }}
                              >
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                    {h.event || t('details.historyEvent')}{atText ? ` - ${atText}` : ''}
                                  </Typography>
                                  {preview ? (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.25 }}>
                                      {preview}
                                    </Typography>
                                  ) : null}
                                  {errorText ? (
                                    <Box
                                      sx={{
                                        mt: 1,
                                        bgcolor: 'background.default',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        borderRadius: 1,
                                        p: 1,
                                        overflowX: 'auto',
                                      }}
                                    >
                                      <Typography component="pre" variant="body2" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                                        {errorText}
                                      </Typography>
                                    </Box>
                                  ) : null}
                                </CardContent>
                              </Card>
                            );
                          })}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('details.noHistory')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ) : null}
            </DialogContent>
          </>
        ) : null}
      </Dialog>

      <Dialog open={contentsDialog.open} onClose={closeContents} maxWidth="lg" fullWidth>
        {contentsDialog.open ? (
          <>
            <DialogTitle>
              {contentsDialog.kind === 'prd' ? t('contents.prdTitle') : t('contents.tasksTitle')} · {contentsDialog.itemId} · {contentsDialog.itemTitle}
            </DialogTitle>
            <DialogContent dividers>
              {contentsDialog.loading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">{t('loading')}</Typography>
                </Stack>
              ) : contentsDialog.error ? (
                <Alert severity="error">{contentsDialog.error}</Alert>
              ) : contentsDialog.contents ? (
                <Box>
                  {(() => {
                    const preview = contentsDialog.kind === 'prd' ? contentsDialog.contents.prd : contentsDialog.contents.tasks;
                    return (
                      <Stack spacing={1}>
                        <Typography variant="caption" color="text.secondary">
                          {preview.path} {preview.exists ? `· ${t('contents.sizeKb', { size: (preview.size_bytes / 1024).toFixed(1) })}` : `· ${t('contents.missing')}`}
                        </Typography>
                        <Box sx={{ bgcolor: 'background.default', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1, p: 1, overflowX: 'auto' }}>
                          <Typography component="pre" variant="body2" sx={{ m: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                            {preview.content || t('page.placeholder')}
                          </Typography>
                        </Box>
                      </Stack>
                    );
                  })()}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('page.placeholder')}
                </Typography>
              )}
            </DialogContent>
          </>
        ) : null}
      </Dialog>
    </Box>
  );
}

